import { useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { BrowserRouter, Navigate, Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import { MutationCache, QueryCache, QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { setUnauthorizedHandler, refreshSession } from './api/client.js';
import { errorCodeOf, toUserMessage } from './api/errors.js';
import { meApi } from './api/me.js';
import { Button } from './components/Button.js';
import { Card } from './components/Card.js';
import { ErrorBoundary } from './components/ErrorBoundary.js';
import { Layout } from './components/Layout.js';
import { Loading } from './components/Loading.js';
import { ToastHost } from './components/Toast.js';
import { AdvisorsPage } from './pages/Advisors.js';
import { AdvisorFeedbackPage } from './pages/AdvisorFeedback.js';
import { AdminPage } from './pages/Admin.js';
import { AssistantPage } from './pages/Assistant.js';
import { CandidatesPage } from './pages/Candidates.js';
import { HomePage } from './pages/Dashboard.js';
import { EventDetailPage } from './pages/EventDetail.js';
import { EventPublicPage } from './pages/EventPublic.js';
import { EventsPage } from './pages/Events.js';
import { NewsPage } from './pages/News.js';
import { EventScreenPage } from './pages/EventScreen.js';
import { ForgotPasswordPage } from './pages/ForgotPassword.js';
import { FindTeammatesPage } from './pages/FindTeammates.js';
import { LandingPage } from './pages/Landing.js';
import { LoginPage } from './pages/Login.js';
import { MatchPage } from './pages/Match.js';
import { MessagesPage } from './pages/Messages.js';
import { PrivacyPage } from './pages/Privacy.js';
import { ProfilePage } from './pages/Profile.js';
import { RegisterPage } from './pages/Register.js';
import { TermsPage } from './pages/Terms.js';
import { SettingsPage } from './pages/Settings.js';
import { TasksPage } from './pages/Tasks.js';
import { TeamPage } from './pages/Team.js';
import { TeamHallPage } from './pages/TeamHall.js';
import { useSession } from './store/session.js';
import { toast } from './store/toast.js';

/**
 * 全局 QueryClient。
 * 关键点：任何查询/变更失败都会弹一次可见 Toast（401 除外，由 API 客户端统一处理清会话 + 跳登录），
 * 保证"不允许静默失败"这条硬要求在全局层面成立，而不依赖每个页面自觉。
 */
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      staleTime: 10_000,
      refetchOnWindowFocus: false,
    },
    mutations: {
      retry: 0,
    },
  },
  queryCache: new QueryCache({
    onError: (error, query) => {
      // meta.silent：装饰性查询（如 Layout 的演示数据角标）失败时静默降级，不打扰用户
      if (query.meta?.silent === true) return;
      if (errorCodeOf(error) === 'UNAUTHORIZED') return;
      toast.error(`加载数据失败：${toUserMessage(error)}`);
    },
  }),
  mutationCache: new MutationCache({
    onError: (error) => {
      if (errorCodeOf(error) === 'UNAUTHORIZED') return;
      toast.error(`操作失败：${toUserMessage(error)}`);
    },
  }),
});

/**
 * 启动时恢复会话：accessToken 只在内存里，刷新页面后必须用 refreshToken 换新的，
 * 再拉一次 GET /me 复核用户信息（服务端为准）。
 */
function SessionBootstrap({ children }: { children: ReactNode }) {
  const setUser = useSession((state) => state.setUser);
  const clear = useSession((state) => state.clear);
  const [restoring, setRestoring] = useState<boolean>(() => {
    const state = useSession.getState();
    return Boolean(state.refreshToken) && !state.accessToken;
  });

  useEffect(() => {
    if (!restoring) return undefined;
    let cancelled = false;
    void (async () => {
      const token = await refreshSession();
      if (cancelled) return;
      if (!token) {
        clear();
        toast.error('登录状态已失效', 'refreshToken 可能已过期或被撤销，请重新登录');
        setRestoring(false);
        return;
      }
      try {
        const me = await meApi.get();
        if (cancelled) return;
        setUser(me.user);
      } catch (error) {
        if (cancelled) return;
        if (errorCodeOf(error) === 'UNAUTHORIZED') clear();
        else toast.error(`读取账号信息失败：${toUserMessage(error)}`, '可以继续操作，稍后重试');
      } finally {
        if (!cancelled) setRestoring(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [restoring, clear, setUser]);

  if (restoring) {
    return (
      <div className="auth-shell">
        <Loading label="正在用 refreshToken 恢复登录状态…" rows={2} />
      </div>
    );
  }
  return <>{children}</>;
}

/** 把「无法恢复的 401」接入路由：清会话后跳登录页（而不是整页刷新） */
function UnauthorizedBridge() {
  const navigate = useNavigate();
  useEffect(() => {
    setUnauthorizedHandler(() => {
      navigate('/login', { replace: true });
    });
    return () => setUnauthorizedHandler(null);
  }, [navigate]);
  return null;
}

/** 登录保护：未登录（无 accessToken 也无 refreshToken）时跳登录，并记住原本要去的页面 */
function RequireAuth() {
  const authed = useSession((state) => Boolean(state.accessToken) || Boolean(state.refreshToken));
  const location = useLocation();
  if (!authed) return <Navigate to="/login" replace state={{ from: `${location.pathname}${location.search}` }} />;
  return <Layout />;
}

/** 首页：未登录显示产品落地页（评委/访客先看到这是什么）；已登录进仪表盘 */
function HomeRoute() {
  const authed = useSession((state) => Boolean(state.accessToken) || Boolean(state.refreshToken));
  return authed ? <Navigate to="/home" replace /> : <LandingPage />;
}

function NotFoundPage() {
  const navigate = useNavigate();
  return (
    <div className="auth-shell">
      <Card className="auth-card" title="页面不存在（404）" subtitle="链接可能已过期或被拼错。">
        <div className="row">
          <Button variant="primary" onClick={() => navigate('/profile')}>
            回到我的画像
          </Button>
          <Button variant="ghost" onClick={() => navigate('/match')}>
            去开始匹配
          </Button>
        </div>
      </Card>
    </div>
  );
}

export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <ErrorBoundary>
          <SessionBootstrap>
            <UnauthorizedBridge />
            <Routes>
              <Route path="/login" element={<LoginPage />} />
              <Route path="/register" element={<RegisterPage />} />
              <Route path="/forgot-password" element={<ForgotPasswordPage />} />
              {/* 双协议公开页（合规基建）：无需登录，注册流程强制勾选后可达 */}
              <Route path="/privacy" element={<PrivacyPage />} />
              <Route path="/terms" element={<TermsPage />} />
              {/* 公开赛事主页（免登录）：组织方分享链接即招生简章，评委扫码即看 */}
              <Route path="/e/:code" element={<EventPublicPage />} />
              {/* 公开首页：未登录看落地页，已登录由 HomeRoute 送回 /profile */}
              <Route path="/" element={<HomeRoute />} />
              {/* 导师一次性反馈页（扩展⑥）：公开免登录，令牌即权限 */}
              <Route path="/feedback/:token" element={<AdvisorFeedbackPage />} />
              <Route element={<RequireAuth />}>
                <Route path="/home" element={<HomePage />} />
                <Route path="/profile" element={<ProfilePage />} />
                {/* 找队友聚合页（三合一）；旧 /match /candidates /hall 路由保留，收藏链接不失效 */}
                <Route path="/find" element={<FindTeammatesPage />} />
                <Route path="/candidates" element={<CandidatesPage />} />
                <Route path="/match" element={<MatchPage />} />
                <Route path="/assistant" element={<AssistantPage />} />
                <Route path="/team" element={<TeamPage />} />
                <Route path="/hall" element={<TeamHallPage />} />
                <Route path="/messages" element={<MessagesPage />} />
                <Route path="/advisors" element={<AdvisorsPage />} />
                <Route path="/tasks" element={<TasksPage />} />
                <Route path="/events" element={<EventsPage />} />
                <Route path="/news" element={<NewsPage />} />
                <Route path="/events/:code" element={<EventDetailPage />} />
                <Route path="/events/:code/screen" element={<EventScreenPage />} />
                <Route path="/settings" element={<SettingsPage />} />
              <Route path="/admin" element={<AdminPage />} />
              </Route>
              <Route path="*" element={<NotFoundPage />} />
            </Routes>
          </SessionBootstrap>
        </ErrorBoundary>
        <ToastHost />
      </BrowserRouter>
    </QueryClientProvider>
  );
}